import { useEffect, useState } from 'react'
import { useTheme } from './theme'

// Read a CSS custom property (e.g., '--c-accent') and convert its rgb-triplet
// value to a "rgb(r g b)" or "rgba(r g b / a)" string usable by SVG/recharts.
function rgbVar(name: string, alpha?: number): string {
  if (typeof window === 'undefined') return 'rgb(0 0 0)'
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  if (!raw) return 'rgb(0 0 0)'
  if (alpha != null) return `rgb(${raw} / ${alpha})`
  return `rgb(${raw})`
}

export interface ThemeColors {
  bgBase: string
  bgElevated: string
  bgOverlay: string
  textPrimary: string
  textSecondary: string
  textTertiary: string
  accent: string
  accentMuted: string
  border: string
  gridLine: string
  tooltipBg: string
  tooltipBorder: string
  success: string
  warning: string
  danger: string
  info: string
}

function readColors(): ThemeColors {
  return {
    bgBase: rgbVar('--c-bg-base'),
    bgElevated: rgbVar('--c-bg-elevated'),
    bgOverlay: rgbVar('--c-bg-overlay'),
    textPrimary: rgbVar('--c-text-primary'),
    textSecondary: rgbVar('--c-text-secondary'),
    textTertiary: rgbVar('--c-text-tertiary'),
    accent: rgbVar('--c-accent'),
    accentMuted: rgbVar('--c-accent', 0.08),
    border: rgbVar('--c-border'),
    gridLine: rgbVar('--c-border', 0.08),
    tooltipBg: rgbVar('--c-bg-elevated'),
    tooltipBorder: rgbVar('--c-border', 0.15),
    success: rgbVar('--c-success'),
    warning: rgbVar('--c-warning'),
    danger: rgbVar('--c-danger'),
    info: rgbVar('--c-info'),
  }
}

/**
 * Returns the current theme's resolved colors. Re-reads when the theme
 * changes, so charts and other render-time consumers stay in sync.
 */
export function useThemeColors(): ThemeColors {
  const theme = useTheme((s) => s.theme)
  const [colors, setColors] = useState<ThemeColors>(() => readColors())
  useEffect(() => {
    // theme is in deps so this re-runs when the user switches theme;
    // we intentionally don't read it inside the effect because readColors()
    // pulls from the live DOM, which is already updated by the theme store.
    void theme
    setColors(readColors())
  }, [theme])
  return colors
}
